import { getProductsDataByColorSelection, getVariantsBySelections } from './product-utils';
//@ts-check
import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import { partition, pickBy, startCase, map } from 'lodash-es';
import {
  Attribute,
  IPdpData,
  IVariant,
  TUserSelections,
  UpdateSelectionFunc,
  onlineSalePriceSetColorData,
} from '../types/PDP';
import ProductImages from './ProductImages';
import { useComputePreviewImages } from './ProductImages/logic';
import Price from './Price';
import AfterPay from './AfterPay';
import ProductInfo from './ProductInfo';
import ProductInfoMobile from './ProductInfoMobile';
import AddToCartUI from './AddToCart';
import { VariantSelections, VariantSelectionsProps } from './VariantSelections';
import { ReturnsSuggestion } from './ReturnsSuggestion';
import { AlmostGoneCopyMessage } from './AlmostGone';
import VirtualStylingHelp from './VirtualStylingHelp';
import { useCheckIfUserIsInUS, useDidUpdateEffect } from '../../_shared/react-hooks';
import { observer } from 'mobx-react-lite';
import { ForcedMatchingSetsVariantSelections } from '../variations/forcedMatchingSets';
import {
  BloomreachPageTypes,
  fireGA4viewProductEvent,
  processSelectedVariantsForGTMUpdates,
  pushBloomreachPageViewEventToDataLayer,
  pushPdpInfoToDatalayer,
} from '../../../utils/gtm-data-layer';
import { AppExlusiveOverlay, calculateIfIsApp } from '../../_shared/ui-components/AppExclusive';
import ProductProvider from '../ProductContext';
import SizeGuide from './SizeGuide';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { computeProductOptionsWithMetadata } from './VariantSelections/helpers';
import { PdpBuilderState } from './PdpBuilder/state';
import { ApolloProvider } from '@apollo/client';
import { productServiceClient } from '../../_shared/apolloClientUtils';
import { RecommendationService } from './SimilarItems/RecommendationServiceCarousel/RecommendationService';
import { useRecommendationServiceCarousel } from './SimilarItems/hooks';
import {
  BOPIS_EXCLUDE_TAG,
  EARLY_ACCESS_TAG,
  EXCHANGE_ONLY_TAG,
  FINAL_SALE_TAG,
  FIS_EXCLUDE_TAG,
  MUI_MOBILE_BREAKPOINT,
  SALE_EXCLUSION_TAG,
  SUPPRESS_MODEL_INFO_TAG,
  HIDDEN_INVENTORY_TAG,
} from 'src/scripts/utils/constants';
import { notNil } from '../../../utils/typescriptUtils';
import { ReviewsBottomLine } from './Reviews';
import {
  determineATCandWishlistEligibility,
  hasHiddenInventoryTag,
  isComingSoon,
  productBadgeText,
} from 'src/scripts/utils/plp-pdp-shared';
import { WishlistButtonPDP } from '../../_shared/wishlist/WishlistButtonPDP';
import { excitingPdpBadgeReplaceRelativePath, isHandbagPDPTemplate } from '../utils';
import { useCartData } from 'src/scripts/utils/cart';
import PurchaseLimitMessaging, {
  extractPurchaseLimitTagData,
  hasMetLimitedProductCartQuantity,
} from '../../_shared/ui-components/PurchaseLimitItem/PurchaseLimitMessaging';
import { FindInStore } from './FindInStore/FindInStore';
import { checkIfCustomer, getOnlineSalePricing, isTagPresent } from '../../../utils/helpers';
import GetTheLookPDP from './GetTheLook/GetTheLookPDP';
import { checkIfInVariation } from 'src/scripts/utils/optimizely';
import ErrorBoundary from 'src/scripts/utils/react/ErrorBoundary';
import { isLocal } from 'src/scripts/utils/environment';
import { ShippingCountryState } from '../../../utils/shipping';
import { prependLocaleToPath } from '../../../utils/localization';
import { pushPageViewEventToFS } from 'src/scripts/utils/full-story';
import { StoreLocatorSidebarPDPWrapper } from './StoreLocator/StoreLocatorSidebarPDPWrapper';
import { BopisContainer } from './Bopis/BopisContainer';
import { bopisState } from './Bopis/bopisState';
import { findInStoreState } from './FindInStore/findInStoreState';
import PDPOmniTabs from './PDPOmniTabs';
import NotifyMe from './NotifyMe';
import { itemIsAccessory } from 'src/scripts/utils/accessories';
import { checkIfProductIsPhasedOut } from './VariantSelections/utils';

const attribsOrder = ['fit', 'fabrication', 'tested & approved', 'materials', 'care'];

let Product = (props: { pdpData: IPdpData; userSelections: TUserSelections; updateSelection: UpdateSelectionFunc }) => {
  // LND-1358 - AB Test: UGC Images in PDP Image Set
  const shouldShowUGCImageInPdp = checkIfInVariation({
    variationId: '30099250839',
    experimentId: '30097280615',
  });

  // LND-2678: [AB Test] PDP Revamp Q3 2025
  const shouldShowOmniPdpExperience = checkIfInVariation({
    variationId: '6333483535040512',
    experimentId: '5195647981191168',
  });

  const { pdpData, userSelections, updateSelection } = props;

  let attributesData: Attribute[];

  if (pdpData.isASet) {
    attributesData = Object.entries(pdpData.pdpProductShell.attributes).map(([label, value]) => ({ label, value }));
  } else {
    const userSelectedColorName = userSelections[0].Color;
    // ? [REVISIT] remove any here
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawAttributesData: any[] = userSelectedColorName
      ? pdpData?.products?.[0].productDataByColorName[userSelectedColorName]?.attributes?.filter(notNil) ?? []
      : [];

    attributesData = rawAttributesData.reduce((acc, node) => {
      if (
        // exclude these metafields from being pulled into the description area
        node?.key.toLowerCase() !== 'quickfit' &&
        node?.key.toLowerCase() !== 'quickfitsizeselector' &&
        node?.key.toLowerCase() !== 'getthelook'
      ) {
        acc.push({ label: node?.key, value: node?.value });
      }
      return acc;
    }, []);
  }

  attributesData.sort((a, b) => attribsOrder.indexOf(a.label) - attribsOrder.indexOf(b.label));

  const [apparelAttribs, beautyAttribs] = partition(attributesData, ({ label }) => {
    return !label.includes('tested & approved');
  });

  // Maximum breakpoint width. Null for the largest (last) breakpoint.
  // The maximum value is calculated as the minimum of the next one less 0.02px
  // to work around the limitations of `min-` and `max-` prefixes and viewports with fractional widths.
  // See https://www.w3.org/TR/mediaqueries-4/#mq-min-max
  // Uses 0.02px rather than 0.01px to work around a current rounding bug in Safari.
  // See https://bugs.webkit.org/show_bug.cgi?id=178261
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true }); // ? [Revisit] Definen generic solution for bp variables

  const needsToDisplayAppExclusive = useMemo(() => {
    const isApp = calculateIfIsApp(navigator.userAgent);
    return !isApp && pdpData.pdpProductShell.tags.some((tag) => tag.toLowerCase() === 'policy:inappexclusive');
  }, [pdpData.pdpProductShell.tags]);

  const pdpImagesPreviewDependency = userSelections[0].Color;

  const strictSelectedVariants = getVariantsBySelections(pdpData, userSelections);

  const variantIdForNonSets = !pdpData.isASet && strictSelectedVariants?.[0]?.id;

  const colorwayProduct = getProductsDataByColorSelection(pdpData, userSelections)?.[0];
  const { handle: handleForNonSets, baseTitle: baseTitleForNonSets, tags: currentTags, id: firstProductId } =
    colorwayProduct || {};
  const hasInventory: boolean = (colorwayProduct?.totalInventory ?? 0) > 0;

  const selectedSize: string | undefined = userSelections[0].Size;

  const excludedFromBopis = colorwayProduct ? isTagPresent(colorwayProduct?.tags, BOPIS_EXCLUDE_TAG) : true;
  const excludedFromFis = colorwayProduct ? isTagPresent(colorwayProduct?.tags, FIS_EXCLUDE_TAG) : true;
  const shouldShowBopis = !pdpData.isASet && bopisState.enabled && !excludedFromBopis;
  const shouldShowFis = !pdpData.isASet && findInStoreState.enabled && !excludedFromFis;

  useDidUpdateEffect(() => {
    if (pdpData.isASet) {
      if (pdpImagesPreviewDependency) {
        replaceUrl(
          `/products/${pdpData.pdpProductShell.handle}?color=${startCase(pdpImagesPreviewDependency).replace(' ', '')}`
        );
      }
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      if (variantIdForNonSets) urlParams.set('variant', `${variantIdForNonSets}`);
      const newPathName = `/products/${handleForNonSets}?${urlParams.toString()}`;
      replaceUrl(newPathName);
    }
  }, [pdpData.isASet, pdpData.products, pdpImagesPreviewDependency, variantIdForNonSets]);

  const looseSelectedVariants = getVariantsBySelections(
    pdpData,
    userSelections.map((selection) => ({ ...selection, Size: undefined })).map(pickBy)
  );
  // seems like there would be lodash helper for this, some kind of 'default' for array or merge or zip, etc.
  const selectedVariants = pdpData.products.map((_, idx) => strictSelectedVariants[idx] || looseSelectedVariants[idx]);
  // - the 0 valid variants case is/should be dealt w/ higher in the chain (PDPContainer)

  // Use ref to make sure that useEffect with "pushPdpInfoToDatalayer" runs only once per SKU
  const SKUref = useRef<string | null>(null);

  useEffect(() => {
    const { isASet, products } = pdpData;
    const firstProductData = products[0];
    const firstVariantData = selectedVariants[0];
    const firstProductColorData = getProductsDataByColorSelection(pdpData, userSelections)?.[0];
    if (
      !isASet &&
      firstProductData &&
      firstVariantData &&
      firstProductColorData &&
      SKUref.current !== firstVariantData.sku
    ) {
      SKUref.current = firstVariantData.sku;
      pushPdpInfoToDatalayer(
        processSelectedVariantsForGTMUpdates(firstProductData, firstVariantData, firstProductColorData)
      );
    }
  }, [pdpData, selectedVariants, userSelections]);

  const { isASet } = pdpData;
  const selectedProdId = colorwayProduct?.id;
  const selectedProdName = colorwayProduct?.title;
  const selectedVariantId = strictSelectedVariants[0]?.id;

  useEffect(() => {
    if (!isASet && selectedProdId && selectedProdName) {
      const bloomreachPdpData = {
        prod_id: selectedProdId,
        prod_name: selectedProdName,
        sku: selectedVariantId ?? '',
      };
      pushBloomreachPageViewEventToDataLayer(BloomreachPageTypes.PDP, bloomreachPdpData);
      //!![REVIST] For FS POC
      pushPageViewEventToFS(bloomreachPdpData);
    }
  }, [selectedProdId, selectedProdName, selectedVariantId, isASet]);

  // GA 4 view_item event
  const selectedColor = userSelections[0].Color;
  useEffect(() => {
    const { isASet } = pdpData;
    if (colorwayProduct && !isASet) {
      fireGA4viewProductEvent([colorwayProduct]);
    } else if (isASet) {
      const productsColorData = getProductsDataByColorSelection(pdpData, userSelections).filter(notNil);
      if (productsColorData) fireGA4viewProductEvent(productsColorData, pdpData.pdpProductShell.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor]); // [REVISIT] Color based subscription only needed. Better implementation to avoid eslint-disable....?

  const [onlineSalePricePresent, onlineSalePriceSetsData] = pdpData.isASet
    ? getOnlineSalePriceDataForSets(pdpData.products)
    : [false, undefined];
  // ? is this "big" enough to be a 'variation'?
  const shouldShowDiscountedSetPricing =
    onlineSalePricePresent || (pdpData.isASet && pdpData.pdpProductShell.tags.includes('Policy:DiscountedSet'));

  const [productActualPrice, displayPrice] = shouldShowDiscountedSetPricing
    ? onlineSalePricePresent && onlineSalePriceSetsData
      ? computeOnlineSalePriceForSets(onlineSalePriceSetsData, selectedVariants, pdpData)
      : computeDiscountedSetPrices(pdpData)
    : computePriceAndStrikethroughPrice(selectedVariants);

  const onlineSalePrice = currentTags ? getOnlineSalePricing(currentTags, productActualPrice) : undefined;
  const actualPrice = onlineSalePrice && !pdpData.isASet ? onlineSalePrice : productActualPrice;

  const isFinalSaleByPriceCalculation = actualPrice <= 0.4 * displayPrice;
  // ? [REVISIT] this is looking at the landedProduct / the setproduct is this intentional?
  const hasTagFinalSale = Boolean(colorwayProduct && colorwayProduct.tags.includes(FINAL_SALE_TAG));
  const isFinalSale = isFinalSaleByPriceCalculation || hasTagFinalSale;
  const isExchangeOnly =
    useCheckIfUserIsInUS() && Boolean(colorwayProduct && colorwayProduct.tags.includes(EXCHANGE_ONLY_TAG));
  const hasSaleExemptionTag = colorwayProduct && colorwayProduct.tags.includes(SALE_EXCLUSION_TAG);

  const isDigitalGood = pdpData.products.every((p) => p.variants.some((v) => !v.requiresShipping));

  const title = pdpData.isASet ? pdpData.pdpProductShell.title : baseTitleForNonSets;
  //each products variant sku urgency.. as well as their inventory urgency(for mobile AB test on drawer)

  // ? [REVISIT] 2 tests in 1 hook is strange, but it's no worse than b4 & this will make it easier to rebase w/ other branches that touch product.js

  const { shouldShowRecommendationServiceCarousel: isYouMayAlsoLikeCarouselReady } = useRecommendationServiceCarousel(
    pdpData.products[0].productType,
    'YouMayAlsoLike'
  );

  const isProductEligibleForYouMayAlsoLike = firstProductId;
  const isProductEligibleForSimilarItems = firstProductId;

  // [REVISIT] Don't really like this approach. Make sure that eligible container has minHeight set to ensure proper anchor linking of "Reviews", and not set in case if additional eligibility requirements aren't met
  useEffect(() => {
    const youMayAlsoLikeContainer = document.getElementById('YouMayAlsoLike');
    const similarItemsContainer = document.getElementById('SimilarItems');
    if (!isProductEligibleForYouMayAlsoLike) {
      if (youMayAlsoLikeContainer) youMayAlsoLikeContainer.style.minHeight = 'initial';
    } else {
      if (youMayAlsoLikeContainer) youMayAlsoLikeContainer.style.minHeight = '360px';
    }
    if (!isProductEligibleForSimilarItems) {
      if (similarItemsContainer) similarItemsContainer.style.minHeight = 'initial';
    } else {
      if (similarItemsContainer) similarItemsContainer.style.minHeight = '360px';
    }
  }, [isProductEligibleForYouMayAlsoLike, isProductEligibleForSimilarItems]);

  const quickFitText = colorwayProduct?.attributes?.find((obj) => obj?.key === 'quickFit')?.value || null;

  const getTheLookProductIds =
    colorwayProduct?.attributes?.find((obj) => obj?.key === 'getTheLook')?.value.split(',') || [];

  // compare cartData with selected variant to see if they have met limited purchase item quantity
  const cartData = useCartData();
  const selectedVariant = getVariantsBySelections(pdpData, userSelections);

  const purchaseLimitTagData = extractPurchaseLimitTagData(currentTags);

  const hasMetATCLimit =
    purchaseLimitTagData && hasMetLimitedProductCartQuantity(cartData, selectedVariant[0]?.sku, purchaseLimitTagData);

  // ? move the "dyanmic" selection below into it's own helper, after deciding on the "Variations" concept
  const variantSelectionProps: VariantSelectionsProps = {
    pdpData,
    userSelections,
    updateSelection,
    actualPrice,
    displayPrice,
  };
  const VariantSelectionUIComponent = pdpData.variations.ForcedMatchingSet
    ? ForcedMatchingSetsVariantSelections
    : VariantSelections;

  const noSelectionOptions = pdpData.products.every((product) =>
    computeProductOptionsWithMetadata(product).every((option) => !option.shouldRenderSelectionsUI)
  );

  const shouldSuppressModelChanger = isTagPresent(colorwayProduct?.tags ?? [], SUPPRESS_MODEL_INFO_TAG);
  const imagesData = useComputePreviewImages(
    pdpData,
    userSelections,
    shouldSuppressModelChanger,
    shouldShowUGCImageInPdp
  );

  const productInfoForStickyButton = {
    variantImage: imagesData.previewImagesToShow[0],
    actualPrice: actualPrice,
    displayPrice: displayPrice,
    isFinalSale: isFinalSale,
    title: title,
  };

  useEffect(() => {
    const pdpProductTags = pdpData.isASet ? pdpData.pdpProductShell.tags : currentTags ?? [];
    PdpBuilderState.setProductTags(pdpProductTags);
    PdpBuilderState.setProductType(pdpData.products[0].productType);
  }, [currentTags, pdpData.isASet, pdpData.pdpProductShell.tags, pdpData.products]);

  const isBelowIsraelThreshold = actualPrice < 75.0;

  const hasInternationalRestrictions = determineATCandWishlistEligibility(pdpData.pdpProductShell.tags);

  const unrestrictedProduct = !hasInternationalRestrictions && !pdpData.isASet;

  const isCustomer = useMemo(() => {
    return checkIfCustomer();
  }, []);

  const isColorwayEarlyAccess = colorwayProduct?.tags.includes(EARLY_ACCESS_TAG) ?? false;

  const isAvailableOnline =
    Boolean(selectedVariant[0]?.availableForSale) && !isTagPresent(colorwayProduct?.tags ?? [], HIDDEN_INVENTORY_TAG);

  const isProductPhasedOut = checkIfProductIsPhasedOut(colorwayProduct?.tags ?? []);
  const shouldHideInventory = hasHiddenInventoryTag(colorwayProduct?.tags ?? []);
  let itemIsProbablyAnAccessory: boolean = itemIsAccessory(pdpData);
  const shouldShowNotifyMe = !isProductPhasedOut && (!isAvailableOnline || shouldHideInventory);

  const pdpAfterPayMessagingShouldShow = pdpAfterPayMessagingToggle && !isHandbagPDPTemplate();
  const ExcitingPdpBadge = () => {
    let badgeHtml = isCustomer ? excitingPdpBadgeSignedIn : excitingPdpBadgeSignedOut;
    if (isMobile && excitingPdpBadgeMobileWebSignedIn && excitingPdpBadgeMobileWebSignedOut) {
      badgeHtml = isCustomer ? excitingPdpBadgeMobileWebSignedIn : excitingPdpBadgeMobileWebSignedOut;
    }
    if (hasSaleExemptionTag || !badgeHtml || badgeHtml.trim() === '') return null;
    return (
      <div
        style={isCustomer ? excitingPdpBadgeSignedInStyles : excitingPdpBadgeSignedOutStyles}
        className="PDPExcitingThing body"
        dangerouslySetInnerHTML={{
          __html: excitingPdpBadgeReplaceRelativePath(badgeHtml),
        }}
      ></div>
    );
  };

  return (
    <ProductProvider>
      {/* // ? [REVISIT] lazyload (SizeGuide) not part of the "critical path" */}
      <SizeGuide productType={pdpData.products[0].productType} quickFitText={quickFitText} />
      <StoreLocatorSidebarPDPWrapper
        product={colorwayProduct}
        selectedVariant={selectedVariant[0]}
        userSelections={userSelections}
        isAvailableOnline={shouldShowBopis ? isAvailableOnline : false}
      />

      <div className="product__wrapper">
        {isMobile ? (
          <div className={`title__wrapper w-100 product-info-cut-version`}>
            <Badges tags={currentTags ? currentTags : []} hasInventory={hasInventory} isCustomer={isCustomer} />
            {/* // ? [REVISIT] sets */}
            <p className="productTitle editorial">{title}</p>
            <Price
              actualPrice={actualPrice}
              displayPrice={displayPrice}
              isFinalSale={isFinalSale}
              product={pdpData.products[0]}
              productHandle={colorwayProduct?.handle}
            />
            {ShippingCountryState.country && ShippingCountryState.country?.dutiesCopy ? (
              <p className="small-p">({ShippingCountryState.country?.dutiesCopy})</p>
            ) : null}
          </div>
        ) : null}
        <ProductImages
          pdpData={pdpData}
          userSelections={userSelections}
          //@ts-expect-error // ? [REVISIT]
          styleId={handleForNonSets && handleForNonSets.split('-')[0]}
          imagesData={imagesData}
          forceSuppressModelChanger={shouldSuppressModelChanger}
        >
          <ImageBadges tags={pdpData.pdpProductShell.tags} />
        </ProductImages>
        <div className={`product__data`} data-is-below-israel-threshold={isBelowIsraelThreshold}>
          {!isMobile ? (
            <div className="title__wrapper w-100">
              <Badges tags={currentTags ? currentTags : []} hasInventory={hasInventory} isCustomer={isCustomer} />
              {/* // ? [REVISIT] sets */}
              <h1 className="productTitle editorial">{title}</h1>
              <Price
                actualPrice={actualPrice}
                displayPrice={displayPrice}
                isFinalSale={isFinalSale}
                product={pdpData.products[0]}
                colorwayProductId={colorwayProduct?.id}
                productHandle={colorwayProduct?.handle}
              />
              {ShippingCountryState.country && ShippingCountryState.country?.dutiesCopy ? (
                <p className="small-p mb-2">({ShippingCountryState.country?.dutiesCopy})</p>
              ) : null}
              {pdpAfterPayMessagingShouldShow &&
                ShippingCountryState.countryCode?.toLowerCase() === 'us' &&
                !shouldShowOmniPdpExperience && <AfterPay price={actualPrice} />}
            </div>
          ) : null}
          <div
            className={`variant-selections ${noSelectionOptions ? 'no-selections' : ''}`}
            data-identifier={`variant-selections`}
          >
            <VariantSelectionUIComponent {...variantSelectionProps} />
            <AlmostGoneCopyMessage pdpData={pdpData} userSelections={userSelections} />
          </div>

          {purchaseLimitTagData ? (
            <PurchaseLimitMessaging
              purchaseLimitTagData={purchaseLimitTagData}
              messageType={hasMetATCLimit ? 'alreadyInBag' : 'default'}
            />
          ) : null}

          {!shouldShowOmniPdpExperience && (
            <div className="add-to-cart-buttons">
              {!needsToDisplayAppExclusive && (
                <>
                  <AddToCartUI
                    pdpData={pdpData}
                    userSelections={userSelections}
                    //@ts-expect-error // ?[REVISIT]
                    currentTags={currentTags}
                    productInfoForStickyButton={productInfoForStickyButton}
                    shouldShowOmniPdpExperience={shouldShowOmniPdpExperience}
                  />
                </>
              )}
              {pdpAfterPayMessagingShouldShow &&
              isMobile &&
              unrestrictedProduct &&
              ShippingCountryState.countryCode?.toLowerCase() === 'us' ? (
                <AfterPay price={actualPrice} shouldShowPrice={true} />
              ) : null}
              {unrestrictedProduct && (
                <WishlistButtonPDP
                  product={pdpData.products[0]}
                  userSelections={userSelections}
                  isCustomer={isCustomer}
                />
              )}
              <ExcitingPdpBadge />
            </div>
          )}

          {/* LND-2678: [AB Test] PDP Revamp Q3 2025 */}
          {shouldShowOmniPdpExperience && (
            <div className="pdp-omni-experience">
              {!isASet && shouldShowNotifyMe && userSelections[0].Size && (
                <div className="notify-enable notify-form-container-0">
                  <NotifyMe
                    customerEmail={pdpData.customer.email}
                    product={pdpData.products[0]}
                    userSelection={userSelections[0]}
                    variant={selectedVariants[0]}
                    itemIsProbablyAnAccessory={itemIsProbablyAnAccessory}
                    uniqueIDforCheckbox={'notify-enable-checkbox'}
                    shouldShowNotifyMe={true}
                    tags={currentTags}
                  />
                </div>
              )}
              {colorwayProduct && (
                <PDPOmniTabs
                  shouldShowBopis={shouldShowBopis}
                  selectedVariant={selectedVariant[0]}
                  quantityAvailable={selectedVariant[0]?.quantityAvailable}
                  product={colorwayProduct}
                  userSelections={userSelections}
                />
              )}
              <div className="pdp-omni-buttons-container">
                <div className="pdp-omni-buttons">
                  <AddToCartUI
                    pdpData={pdpData}
                    userSelections={userSelections}
                    //@ts-expect-error // ?[REVISIT]
                    currentTags={currentTags}
                    productInfoForStickyButton={productInfoForStickyButton}
                    shouldShowOmniPdpExperience={shouldShowOmniPdpExperience}
                  />
                  <WishlistButtonPDP
                    product={pdpData.products[0]}
                    userSelections={userSelections}
                    isCustomer={isCustomer}
                    shouldShowOmniPdpExperience={shouldShowOmniPdpExperience}
                  />
                </div>
                {!hasSaleExemptionTag && <ExcitingPdpBadge />}
                {pdpAfterPayMessagingShouldShow &&
                  unrestrictedProduct &&
                  ShippingCountryState.countryCode?.toLowerCase() === 'us' && (
                    <AfterPay price={actualPrice} shouldShowPrice={true} />
                  )}
              </div>
            </div>
          )}

          {isMobile && colorwayProduct && (
            <>
              {!shouldShowOmniPdpExperience && (
                <>
                  {shouldShowFis && (
                    <FindInStore
                      selectedVariant={selectedVariant[0]}
                      quantityAvailable={selectedVariant[0]?.quantityAvailable}
                      product={colorwayProduct}
                      userSelections={userSelections}
                    />
                  )}
                  {shouldShowBopis && (
                    <BopisContainer
                      selectedVariant={selectedVariant[0]}
                      userSelections={userSelections}
                      product={colorwayProduct}
                    />
                  )}
                  {!isDigitalGood && isMobile ? (
                    <div className="icons-box">
                      <ReturnsSuggestion
                        isFinalSale={isFinalSale}
                        isExchangeOnly={isExchangeOnly}
                        productTag={currentTags}
                      />
                    </div>
                  ) : null}
                </>
              )}
              {getTheLookProductIds ? (
                <GetTheLookPDP showEarlyAccess={isColorwayEarlyAccess} productIds={getTheLookProductIds} />
              ) : null}
            </>
          )}

          {pdpData.pdpProductShell.stylistChat.isEnabled && <VirtualStylingHelp pdpData={pdpData} />}
          {isMobile && (
            <>
              <ProductInfoMobile pdpData={pdpData} apparelAttribs={apparelAttribs} beautyAttribs={beautyAttribs} />
            </>
          )}
          {!isMobile && colorwayProduct && (
            <>
              {!shouldShowOmniPdpExperience && (
                <>
                  {shouldShowFis && (
                    <FindInStore
                      selectedVariant={selectedVariant[0]}
                      quantityAvailable={selectedVariant[0]?.quantityAvailable}
                      product={colorwayProduct}
                      userSelections={userSelections}
                    />
                  )}
                  {shouldShowBopis && (
                    <BopisContainer
                      selectedVariant={selectedVariant[0]}
                      product={colorwayProduct}
                      userSelections={userSelections}
                    />
                  )}
                  {!isDigitalGood && !isMobile ? (
                    <div className="icons-box">
                      <ReturnsSuggestion
                        isFinalSale={isFinalSale}
                        isExchangeOnly={isExchangeOnly}
                        productTag={currentTags}
                      />
                    </div>
                  ) : null}
                </>
              )}
              {getTheLookProductIds ? (
                <GetTheLookPDP showEarlyAccess={isColorwayEarlyAccess} productIds={getTheLookProductIds} />
              ) : null}
            </>
          )}
        </div>
      </div>
      {isMobile ? (
        // [REVISIT] Is that good that ReviewsBottomLine is initiating rendering of other reviews components that depend on the data from Yotpo API; Needs better architecture
        !isHandbagPDPTemplate() ? (
          <ReviewsBottomLine
            colorwayProductId={colorwayProduct?.id}
            isStandaloneVersion={true}
            productHandle={colorwayProduct?.handle}
          />
        ) : null
      ) : (
        <>
          <ProductInfo pdpData={pdpData} apparelAttribs={apparelAttribs} beautyAttribs={beautyAttribs} />
        </>
      )}
      {needsToDisplayAppExclusive && <AppExlusiveOverlay className="product__appExclusiveOverlay" />}
      <ErrorBoundary className="centered-message" key={(!isLocal || new Date()).toString()}>
        <ApolloProvider client={productServiceClient}>
          {/* hide Similar Items & YouMayAlsoLike on Handbag PDP */}
          {isProductEligibleForSimilarItems && firstProductId && !isHandbagPDPTemplate() ? (
            <RecommendationService
              productId={firstProductId}
              selectedSize={selectedSize}
              recommendationScheme="similar_items_2021_a"
              portalTargetId="SimilarItems"
              blockTitle="Similar Items"
            />
          ) : null}
          {isProductEligibleForYouMayAlsoLike &&
          isYouMayAlsoLikeCarouselReady &&
          firstProductId &&
          !isHandbagPDPTemplate() ? (
            <RecommendationService
              productId={firstProductId}
              recommendationScheme="ymal_2021_a"
              portalTargetId="YouMayAlsoLike"
              blockTitle={getYouMayAlsoLikeVariantTitleCopy()}
            />
          ) : null}
        </ApolloProvider>
      </ErrorBoundary>
    </ProductProvider>
  );
};
Product = observer(Product);

export default Product;

// helper functions
const replaceUrl = (url: string) => {
  window.history.replaceState(null, window.document.title, prependLocaleToPath(url));
};

const yaml_variantsTitleCopyMapping: { [index: string]: string } = {
  '25228450507': 'Make a match',
  '25245891516': 'Recommended for you',
  '25218900953': 'Customers also viewed',
  '25294310004': 'Popular pairings',
};

const getYouMayAlsoLikeVariantTitleCopy = () => {
  const variantID = Object.keys(yaml_variantsTitleCopyMapping).find((variant) =>
    checkIfInVariation({ variationId: variant, experimentId: '25242100448' })
  );
  return (variantID && yaml_variantsTitleCopyMapping[variantID]) || 'You may also Like';
};

type PriceStrikethroughPrice = [actualPrice: number, strikethoughPrice: number];

const computePriceAndStrikethroughPrice = (variants: (IVariant | undefined)[]): PriceStrikethroughPrice =>
  variants.reduce(
    ([priceSum, displayPriceSum], variant) => {
      // ? [REVISIT] is defaulting to 0 correct?
      const { price = 0, displayPrice } = variant || {};
      return [priceSum + price, displayPriceSum + (displayPrice || price)];
    },
    [0, 0]
  );

const computeDiscountedSetPrices = (pdpData: IPdpData): PriceStrikethroughPrice => {
  const { price, compareAtPrice } = pdpData.pdpProductShell;
  return [price, compareAtPrice || price];
};

const getOnlineSalePriceDataForSets = (products: IPdpData['products']): [boolean, onlineSalePriceSetColorData[]] => {
  let onlineSalePriceTagPresent = false;
  const onlineSalePriceSetsData = products.map((product) => {
    let colorContainer: onlineSalePriceSetColorData = {};
    map(product.productDataByColorName, (color) => {
      const onlineSalePrice = getOnlineSalePricing(color.tags, color?.variants[0]?.price);
      if (!!onlineSalePrice) {
        onlineSalePriceTagPresent = true;
      }
      return (colorContainer[color.colorName] = onlineSalePrice);
    });
    return colorContainer;
  });

  return [onlineSalePriceTagPresent, onlineSalePriceSetsData];
};

const determineVariantPrices = (item: onlineSalePriceSetColorData | undefined, variant: IVariant | undefined) => {
  const onlineSalePrice = item && variant && item[variant.selectionOptions.Color];
  const [price, compareAtPrice] = onlineSalePrice
    ? [onlineSalePrice, variant?.price]
    : [variant?.price, variant?.displayPrice || variant?.price];
  return [price, compareAtPrice];
};

const computeOnlineSalePriceForSets = (
  onlineSalePriceSetsData: onlineSalePriceSetColorData[],
  variants: (IVariant | undefined)[],
  pdpData: IPdpData
): PriceStrikethroughPrice => {
  const [firstVariant, secondVariant] = variants;
  const [firstItem, secondItem] = onlineSalePriceSetsData;

  const [firstVariantPrice, firstVariantCompareAtPrice] = determineVariantPrices(firstItem, firstVariant);
  const [secondVariantPrice, secondVariantCompareAtPrice] = determineVariantPrices(secondItem, secondVariant);
  return firstVariantPrice && secondVariantPrice && firstVariantCompareAtPrice && secondVariantCompareAtPrice
    ? [firstVariantPrice + secondVariantPrice, firstVariantCompareAtPrice + secondVariantCompareAtPrice]
    : computeDiscountedSetPrices(pdpData);
};

const pdpStylingToBadge = (badge: string, isCustomer: boolean) => {
  return excitingBadgeStyleEnabled && badge.includes('%')
    ? isCustomer
      ? { ...excitingPdpBadgeSignedInStyles, padding: '4px 8px', fontWeight: 400 }
      : { ...excitingPdpBadgeSignedOutStyles, padding: '4px 8px', fontWeight: 400 }
    : undefined;
};
// sub components
const Badges = React.memo((props: { tags: string[]; hasInventory: boolean; isCustomer: boolean }) => {
  const _isComingSoon = isComingSoon(props.tags);
  const selectedBadge = _isComingSoon ? 'Coming Soon' : productBadgeText(props.tags, props.hasInventory);
  return selectedBadge && selectedBadge.length > 0 ? (
    <div className="product__badge tag" style={pdpStylingToBadge(selectedBadge, props.isCustomer)}>
      {selectedBadge}
    </div>
  ) : null;
});

// sub components
// * Nest ImageBadge in the "product__data sticky" product details component?
const ImageBadges = React.memo(
  //@ts-ignore
  (props: { tags: string[] }): ReactNode => {
    const hasReversibleTag = props.tags.find((tag) => tag === 'ProductAttribute:Reversible') !== undefined;
    return hasReversibleTag && <div className="PDPReversibleBadge">Reversible!</div>;
  }
);

const {
  excitingPdpBadgeSignedOutForeground,
  excitingPdpBadgeSignedOutBackground,
  excitingPdpBadgeSignedOut,
  excitingPdpBadgeSignedIn,
  excitingPdpBadgeSignedInForeground,
  excitingPdpBadgeSignedInBackground,
  pdpAfterPayMessagingToggle,
  excitingPdpBadgeMobileWebSignedOut,
  excitingPdpBadgeMobileWebSignedIn,
  excitingBadgeStyleEnabled,
} = window.getSiteSettings();
const excitingPdpBadgeSignedOutStyles = {
  color: excitingPdpBadgeSignedOutForeground,
  backgroundColor: excitingPdpBadgeSignedOutBackground,
};

const excitingPdpBadgeSignedInStyles = {
  color: excitingPdpBadgeSignedInForeground,
  backgroundColor: excitingPdpBadgeSignedInBackground,
};
